import { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { enterpriseDetailsSelector } from "../../Reducers/enterpriseDetailsReducer";
import { enterpriseLevelsDetailsSelector } from "../../Reducers/enterpriseLevelsDetailsReducer";
import styles from "./accounts.module.css";
import GenericModal from "../CommonComponents/genericModal/GenericModal";
import { ENTERPRISE } from "../../Enums/administratorLevels";
import CrossIcon from "../Icons/cross-icon";

export const CircleRadioIcon = ({ selected }) => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
    >
      <path
        d="M12.0017 21.5C10.6877 21.5 9.45267 21.2507 8.2965 20.752C7.14033 20.2533 6.13467 19.5766 5.2795 18.7218C4.42433 17.8669 3.74725 16.8617 3.24825 15.706C2.74942 14.5503 2.5 13.3156 2.5 12.0017C2.5 10.6877 2.74933 9.45267 3.248 8.2965C3.74667 7.14033 4.42342 6.13467 5.27825 5.2795C6.13308 4.42433 7.13833 3.74725 8.294 3.24825C9.44967 2.74942 10.6844 2.5 11.9983 2.5C13.3123 2.5 14.5473 2.74933 15.7035 3.248C16.8597 3.74667 17.8653 4.42342 18.7205 5.27825C19.5757 6.13308 20.2528 7.13833 20.7518 8.294C21.2506 9.44967 21.5 10.6844 21.5 11.9983C21.5 13.3123 21.2507 14.5473 20.752 15.7035C20.2533 16.8597 19.5766 17.8653 18.7218 18.7205C17.8669 19.5757 16.8617 20.2528 15.706 20.7518C14.5503 21.2506 13.3156 21.5 12.0017 21.5ZM12 20C14.2333 20 16.125 19.225 17.675 17.675C19.225 16.125 20 14.2333 20 12C20 9.76667 19.225 7.875 17.675 6.325C16.125 4.775 14.2333 4 12 4C9.76667 4 7.875 4.775 6.325 6.325C4.775 7.875 4 9.76667 4 12C4 14.2333 4.775 16.125 6.325 17.675C7.875 19.225 9.76667 20 12 20Z"
        fill="#008062"
      />
      {selected && (
        <path
          d="M12 16.5C13.2487 16.5 14.3108 16.0622 15.1865 15.1865C16.0622 14.3108 16.5 13.2487 16.5 12C16.5 10.7513 16.0622 9.68917 15.1865 8.8135C14.3108 7.93783 13.2487 7.5 12 7.5C10.7513 7.5 9.68917 7.93783 8.8135 8.8135C7.93783 9.68917 7.5 10.7513 7.5 12C7.5 13.2487 7.93783 14.3108 8.8135 15.1865C9.68917 16.0622 10.7513 16.5 12 16.5Z"
          fill="#008062"
        />
      )}
    </svg>
  );
};

const AddAccountModal = ({ open, onClose }) => {
    const enterpriseDetails = useSelector(enterpriseDetailsSelector);
    const enterpriseLevelsDetails = useSelector(enterpriseLevelsDetailsSelector);
    const [copied, setCopied] = useState(false);
    const [selectedSubRegion, setSelectedSubRegion] = useState(null);

    const handleCopy = useCallback(() => {
        if (!enterpriseLevelsDetails || !enterpriseLevelsDetails.subRegionInfo) return;
        const url = enterpriseLevelsDetails.subRegionInfo[selectedSubRegion]?.accountCreationUrl;
        if (url) {
            navigator.clipboard.writeText(url); 
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        }
    }, [enterpriseLevelsDetails, selectedSubRegion]);

    const handleCreate = useCallback(() => {
        if (!enterpriseLevelsDetails || !enterpriseLevelsDetails.subRegionInfo) return;
        const url = enterpriseLevelsDetails.subRegionInfo[selectedSubRegion]?.accountCreationUrl;
        if (url) {
            window.open(url, '_blank');
        }
    }, [enterpriseLevelsDetails, selectedSubRegion]);

    useEffect(() => {
        if (enterpriseLevelsDetails && enterpriseLevelsDetails.subRegionInfo) {
            setSelectedSubRegion(Object.values(enterpriseLevelsDetails.subRegionInfo)[0].id);
        }
    }, [enterpriseLevelsDetails]);

    if (!enterpriseDetails || !enterpriseLevelsDetails) return null;
    if (enterpriseDetails.getAdminLevel() !== ENTERPRISE) return null;
    if (!enterpriseLevelsDetails.subRegionInfo) return null;

    return (
        <GenericModal open={open} onClose={onClose} backdropClassName={styles.addAccountModalBackdrop}>
            <div className={styles.addAccountModalContainer}>
                <div className={styles.addAccountModalHeader}>
                    <div className={styles.addAccountModalTitle}>Add account</div>
                    <button className={styles.addAccountModalClose} onClick={onClose} aria-label="Close">
                        <CrossIcon />
                    </button>
                </div>
                <div className={styles.addAccountModalBody}>
                    <div className={styles.addAccountModalDesc}>
                        Choose a subregion and click <span className={styles.addAccountModalDescStrong}>Create account</span> to start the account creation process. The account will be created under your selected subregion. Or copy the signup link to share or use later.
                    </div>
                    <div className={styles.addAccountModalSubregionList}>
                        {Object.values(enterpriseLevelsDetails.subRegionInfo).map(subRegion => (
                            <div key={subRegion.id} className={styles.addAccountModalSubregionRow} onClick={() => setSelectedSubRegion(subRegion.id)}>
                                <CircleRadioIcon selected={selectedSubRegion === subRegion.id} />
                                <div className={styles.addAccountModalSubregionName}>{subRegion.name}</div>
                            </div>
                        ))}
                    </div>
                </div>
                <div className={styles.addAccountModalFooter}>
                    <button
                        className={styles.addAccountModalCancelBtn}
                        onClick={onClose}
                    >
                        Cancel
                    </button>

                    <div className={styles.addAccountModalFooterRight}>
                        <button
                            className={styles.addAccountModalCopyBtn}
                            onClick={handleCopy}
                        >
                            {copied ? 'Link Copied!' : 'Copy signup link'}
                        </button>
                        <button
                            className={styles.addAccountModalCreateBtn}
                            onClick={handleCreate}
                        >
                            Create account
                        </button>
                    </div>
                </div>
            </div>
        </GenericModal>
    );
};

export default AddAccountModal;
